import { KLASOUZYTKI__getBaseSymbols } from "../egib/klasouzytki/utils";
import { MPZP_STREFA_BY_PRZEZNACZENIE } from "../mpzp/constants";
import type { ParcelFilters } from ".";

// The tiles carry parcel__zabytki as `true` (only on parcels that have one), the FGB as the monument
// text — both count as presence.
function hasZabytki(props: GeoJSON.GeoJsonProperties): boolean {
	const zabytki = props?.parcel__zabytki;
	return zabytki !== undefined && zabytki !== null && zabytki !== "" && zabytki !== false;
}

// The tiles omit a NULL pum, the FGB may carry it as null — neither is a number.
function hasPum(value: unknown): boolean {
	return value !== undefined && value !== null && value !== "" && Number.isFinite(Number(value)) === true;
}

/**
 * range -> [min, max]
 * set -> symbols
 * flag -> boolean
 * when the parcel lacks it (e.g. no POG) -> null
 */
const PARCEL_FILTER_VALUE_READERS: Record<keyof ParcelFilters, (props: GeoJSON.GeoJsonProperties) => [number, number] | string[] | boolean | null> = {
	parcel__pole_geometryczne: (props) => {
		const value = Number(props?.parcel__pole_geometryczne);
		return Number.isFinite(value) === true ? [value, value] : null;
	},
	parcel__grupa_rejestrowa: (props) => [String(props?.parcel__grupa_rejestrowa)],
	parcel__klasouzytki_egib: (props) => KLASOUZYTKI__getBaseSymbols(String(props?.parcel__klasouzytki_egib ?? "")),
	pog__symbol: (props) => {
		const symbols = props?.pog__symbol;
		return symbols === undefined || symbols === null || symbols === "" ? [] : String(symbols).split(";");
	},
	pog__maksWysokoscZabudowy: (props) => {
		const min = Number(props?.pog__maksWysokoscZabudowy_min);
		const max = Number(props?.pog__maksWysokoscZabudowy_max);
		return Number.isFinite(min) === true && Number.isFinite(max) === true ? [min, max] : null;
	},
	pog__maksNadziemnaIntensywnoscZabudowy: (props) => {
		const min = Number(props?.pog__maksNadziemnaIntensywnoscZabudowy_min);
		const max = Number(props?.pog__maksNadziemnaIntensywnoscZabudowy_max);
		return Number.isFinite(min) === true && Number.isFinite(max) === true ? [min, max] : null;
	},
	pog__maksUdzialPowierzchniZabudowy: (props) => {
		const min = Number(props?.pog__maksUdzialPowierzchniZabudowy_min);
		const max = Number(props?.pog__maksUdzialPowierzchniZabudowy_max);
		return Number.isFinite(min) === true && Number.isFinite(max) === true ? [min, max] : null;
	},
	pog__minUdzialPowierzchniBiologicznieCzynnej: (props) => {
		const min = Number(props?.pog__minUdzialPowierzchniBiologicznieCzynnej_min);
		const max = Number(props?.pog__minUdzialPowierzchniBiologicznieCzynnej_max);
		return Number.isFinite(min) === true && Number.isFinite(max) === true ? [min, max] : null;
	},
	pog__pum: (props) => {
		const value = Number(props?.pog__pum);
		return Number.isFinite(value) === true ? [value, value] : null;
	},
	pog__zPum: (props) => hasPum(props?.pog__pum),
	pog__bezPum: (props) => hasPum(props?.pog__pum) === false,
	mpzp__strefa: (props) => {
		const przeznaczenie = props?.mpzp__przeznaczenie;
		if (przeznaczenie === undefined || przeznaczenie === null || przeznaczenie === "") {
			return [];
		}
		const symbols = String(przeznaczenie)
			.split(";")
			.map((value) => MPZP_STREFA_BY_PRZEZNACZENIE[value])
			.filter((symbol) => symbol !== undefined);
		return [...new Set(symbols)];
	},
	// The enhance writes the designation whenever the parcel touched any MPZP terrain, so its presence is
	// what says "a plan applies here". mpzp__pum would be wrong: it only exists where the uchwała gave an
	// intensity, which leaves out roads and every terrain without parameters.
	mpzp__inArea: (props) => {
		const przeznaczenie = props?.mpzp__przeznaczenie;
		return przeznaczenie !== undefined && przeznaczenie !== null && przeznaczenie !== "";
	},
	mpzp__pum: (props) => {
		const value = Number(props?.mpzp__pum);
		return Number.isFinite(value) === true ? [value, value] : null;
	},
	mpzp__zPum: (props) => hasPum(props?.mpzp__pum),
	mpzp__bezPum: (props) => hasPum(props?.mpzp__pum) === false,
	pum__diff: (props) => {
		const pog = Number(props?.pog__pum);
		const mpzp = Number(props?.mpzp__pum);
		if (Number.isFinite(pog) === false || Number.isFinite(mpzp) === false) {
			return null;
		}
		const diff = Math.abs(pog - mpzp);
		return [diff, diff];
	},
	parcel__zabytki: (props) => hasZabytki(props),
	parcel__bezZabytkow: (props) => hasZabytki(props) === false,
	parcel__zabudowana: (props) => {
		const count = Number(props?.parcel__budynki_liczba);
		return Number.isFinite(count) === true && count > 0;
	},
	// Only a known 0 counts as empty — a missing count means "no buildings data", not "no buildings".
	parcel__niezabudowana: (props) => {
		const count = props?.parcel__budynki_liczba;
		return count !== undefined && count !== null && count !== "" && Number(count) === 0;
	},
	obszar_uzupelnienia_zabudowy__inArea: (props) => {
		const oznaczenie = props?.obszar_uzupelnienia_zabudowy__oznaczenie;
		return oznaczenie !== undefined && oznaczenie !== null && oznaczenie !== "";
	},
};

export { PARCEL_FILTER_VALUE_READERS };
